import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";

type Period = "today" | "7d" | "30d" | "month" | "previous";
type DailyPoint = { day: string; leads: number; stores: number };
type Metrics = {
  leads: number;
  newLeads: number;
  followUpLeads: number;
  stores: number;
  publishedStores: number;
  newStores: number;
  pendingSubmissions: number;
  openInvitations: number;
  expiredInvitations: number;
  daily: DailyPoint[];
};
type RecentLead = { id: string; name: string; service: string | null; status: string; created_at: string };
type RecentSubmission = {
  id: string;
  name: string;
  status: string;
  created_at: string;
  edit_features: string[] | null;
};

const periods: { id: Period; label: string }[] = [
  { id: "today", label: "Hoy" },
  { id: "7d", label: "7 días" },
  { id: "30d", label: "30 días" },
  { id: "month", label: "Este mes" },
  { id: "previous", label: "Mes anterior" },
];

function bounds(period: Period) {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  if (period === "today") start.setHours(0, 0, 0, 0);
  if (period === "7d") {
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  }
  if (period === "30d") {
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  }
  if (period === "month") start.setDate(1);
  if (period === "previous") {
    start.setMonth(start.getMonth() - 1, 1);
    start.setHours(0, 0, 0, 0);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);
  }
  return { start: start.toISOString(), end: end.toISOString() };
}

async function fetchDashboard(period: Period) {
  const { start, end } = bounds(period);
  const [metricResult, leadsResult, submissionsResult] = await Promise.all([
    supabase.rpc("admin_dashboard_metrics" as never, { p_start: start, p_end: end } as never),
    supabase
      .from("leads")
      .select("id,name,service,status,created_at")
      .order("created_at", { ascending: false })
      .limit(6),
    supabase
      .from("directory_submissions")
      .select("id,name,status,created_at,edit_features")
      .in("status", ["pending", "received", "in_review"])
      .order("created_at", { ascending: false })
      .limit(5),
  ]);
  for (const result of [metricResult, leadsResult, submissionsResult]) if (result.error) throw result.error;
  return {
    metrics: metricResult.data as unknown as Metrics,
    leads: (leadsResult.data ?? []) as RecentLead[],
    submissions: (submissionsResult.data ?? []) as RecentSubmission[],
    updatedAt: new Date(),
  };
}

function formatDate(value: string | Date) {
  return new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
    ...(value instanceof Date ? {} : { timeStyle: "short" }),
  }).format(new Date(value));
}
function formatNumber(value: number) {
  return new Intl.NumberFormat("es-PE").format(value);
}

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={`rounded-2xl border border-slate-200/80 bg-white shadow-[0_3px_14px_rgba(15,23,42,0.035)] ${className}`}
    >
      {children}
    </section>
  );
}

function MetricCard({
  label,
  value,
  detail,
  icon,
  accent,
}: {
  label: string;
  value: number;
  detail: string;
  icon: string;
  accent: string;
}) {
  return (
    <Card className="p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.13em] text-slate-500">{label}</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">{formatNumber(value)}</p>
        </div>
        <span className={`grid h-10 w-10 place-items-center rounded-xl text-sm font-bold ${accent}`}>
          {icon}
        </span>
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-500">{detail}</p>
    </Card>
  );
}

function TrendChart({ points }: { points: DailyPoint[] }) {
  const width = 680,
    height = 208,
    left = 34,
    right = 10,
    top = 12,
    bottom = 28;
  const max = Math.max(1, ...points.flatMap((p) => [p.leads, p.stores]));
  const x = (index: number) =>
    left + (points.length <= 1 ? 0 : (index * (width - left - right)) / (points.length - 1));
  const y = (value: number) => height - bottom - (value / max) * (height - top - bottom);
  const line = (key: "leads" | "stores") =>
    points.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p[key])}`).join(" ");
  const labels =
    points.length > 10
      ? points.filter((_, i) => i === 0 || i === points.length - 1 || i % Math.ceil(points.length / 5) === 0)
      : points;
  if (!points.length || points.every((point) => point.leads === 0 && point.stores === 0))
    return (
      <div className="grid h-56 place-items-center text-sm text-slate-500">
        Sin datos suficientes para el periodo.
      </div>
    );
  return (
    <div className="w-full overflow-hidden">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Tendencia de leads nuevos y negocios incorporados"
        className="h-56 w-full overflow-visible"
      >
        {[0, 1, 2, 3].map((n) => {
          const gy = top + (n * (height - top - bottom)) / 3;
          return (
            <g key={n}>
              <line x1={left} x2={width - right} y1={gy} y2={gy} stroke="#e9eef4" strokeDasharray="3 5" />
              <text x={left - 9} y={gy + 4} textAnchor="end" fill="#94a3b8" fontSize="10">
                {Math.round((max * (3 - n)) / 3)}
              </text>
            </g>
          );
        })}
        <path
          d={line("leads")}
          fill="none"
          stroke="#2563eb"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={line("stores")}
          fill="none"
          stroke="#10b981"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((p, i) => (
          <g key={p.day}>
            <circle cx={x(i)} cy={y(p.leads)} r="3" fill="#2563eb" />
            <circle cx={x(i)} cy={y(p.stores)} r="3" fill="#10b981" />
          </g>
        ))}
        {labels.map((p) => {
          const i = points.indexOf(p);
          return (
            <text key={p.day} x={x(i)} y={height - 7} textAnchor="middle" fill="#94a3b8" fontSize="10">
              {new Intl.DateTimeFormat("es-PE", { day: "2-digit", month: "short" }).format(
                new Date(`${p.day}T12:00:00`),
              )}
            </text>
          );
        })}
      </svg>
      <div className="mt-2 flex gap-5 text-xs text-slate-600">
        <span className="inline-flex items-center gap-2">
          <i className="h-2 w-2 rounded-full bg-blue-600" />
          Leads recibidos
        </span>
        <span className="inline-flex items-center gap-2">
          <i className="h-2 w-2 rounded-full bg-emerald-500" />
          Negocios incorporados
        </span>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const [period, setPeriod] = useState<Period>("30d");
  const queryKey = useMemo(() => ["dashboard", period], [period]);
  const { data, isLoading, error, dataUpdatedAt, refetch } = useQuery({
    queryKey,
    queryFn: () => fetchDashboard(period),
  });
  const m = data?.metrics;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 pb-8">
      <header className="flex flex-col gap-5 border-b border-slate-200 pb-6 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
            VIMOS DIGITAL <span className="mx-1 text-slate-300">/</span> CENTRO DE CONTROL
          </p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Resumen ejecutivo
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Estado comercial y operativo del directorio, con datos verificados desde Supabase.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between xl:justify-end">
          <div className="text-xs text-slate-500 sm:text-right">
            <span className="block font-semibold text-slate-700">{formatDate(new Date())}</span>
            <span>Actualizado {dataUpdatedAt ? formatDate(new Date(dataUpdatedAt)) : "al cargar"}</span>
          </div>
          <button
            onClick={() => void refetch()}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
          >
            Actualizar datos
          </button>
        </div>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Indicadores del periodo</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Los totales actuales del directorio se mantienen como contexto.
          </p>
        </div>
        <div
          className="flex max-w-full gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1"
          role="group"
          aria-label="Periodo del dashboard"
        >
          {periods.map((item) => (
            <button
              key={item.id}
              onClick={() => setPeriod(item.id)}
              aria-pressed={period === item.id}
              className={`whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold transition ${period === item.id ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-36 animate-pulse rounded-2xl bg-slate-200/70" />
          ))}
        </div>
      ) : null}
      {error ? (
        <Card className="flex flex-col gap-3 border-rose-200 bg-rose-50 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold text-rose-900">No se pudo actualizar el dashboard</p>
            <p className="mt-1 text-sm text-rose-700">
              Revisa tu acceso o vuelve a intentar. No mostramos cifras parciales como si fueran completas.
            </p>
          </div>
          <button
            onClick={() => void refetch()}
            className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-800"
          >
            Reintentar
          </button>
        </Card>
      ) : null}

      {m ? (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Leads recibidos"
              value={m.leads}
              detail={`${m.newLeads} nuevos · ${m.followUpLeads} en seguimiento o cotización`}
              icon="L"
              accent="bg-blue-50 text-blue-700"
            />
            <MetricCard
              label="Negocios publicados"
              value={m.publishedStores}
              detail={`De ${formatNumber(m.stores)} negocios registrados en total`}
              icon="D"
              accent="bg-emerald-50 text-emerald-700"
            />
            <MetricCard
              label="Nuevas incorporaciones"
              value={m.newStores}
              detail="Negocios creados en el periodo; las ediciones no cuentan"
              icon="+"
              accent="bg-violet-50 text-violet-700"
            />
            <MetricCard
              label="Solicitudes por revisar"
              value={m.pendingSubmissions}
              detail={`${m.openInvitations} invitaciones vigentes · ${m.expiredInvitations} vencidas`}
              icon="↗"
              accent={m.pendingSubmissions ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}
            />
          </section>

          <section className="grid gap-5 xl:grid-cols-[minmax(0,1.55fr)_minmax(300px,0.8fr)]">
            <Card className="p-5 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                    Adquisición y crecimiento
                  </p>
                  <h2 className="mt-1 text-lg font-semibold text-slate-950">Actividad del directorio</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Eventos diarios del rango seleccionado · conteo de registros, sin estimaciones
                  </p>
                </div>
                <span className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
                  {periods.find((p) => p.id === period)?.label}
                </span>
              </div>
              <div className="mt-5">
                <TrendChart points={m.daily ?? []} />
              </div>
            </Card>
            <Card className="overflow-hidden">
              <div className="border-b border-slate-100 px-5 py-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                  Cola de trabajo
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-950">Requiere atención</h2>
                <p className="mt-1 text-xs text-slate-500">
                  Solicitudes abiertas que esperan revisión del equipo.
                </p>
              </div>
              {data.submissions.length ? (
                <ul className="divide-y divide-slate-100">
                  {data.submissions.slice(0, 4).map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-3 px-5 py-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800">{s.name}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {s.edit_features ? "Actualización de ficha" : "Nueva incorporación"} ·{" "}
                          {formatDate(s.created_at)}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                        {s.status === "changes_requested" ? "Cambios" : "Revisar"}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-5 py-8 text-center">
                  <span className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-emerald-50 text-emerald-700">
                    ✓
                  </span>
                  <p className="mt-3 text-sm font-semibold text-slate-800">Todo al día</p>
                  <p className="mt-1 text-xs text-slate-500">No hay solicitudes pendientes de revisión.</p>
                </div>
              )}
              <div className="border-t border-slate-100 px-5 py-4">
                <Link to="/directorio" className="text-sm font-semibold text-blue-700 hover:text-blue-900">
                  Abrir operaciones del directorio <span aria-hidden="true">→</span>
                </Link>
              </div>
            </Card>
          </section>

          <section className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(300px,0.8fr)]">
            <Card className="overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-5">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                    Bandeja comercial
                  </p>
                  <h2 className="mt-1 text-lg font-semibold text-slate-950">Leads recientes</h2>
                </div>
                <Link to="/leads" className="text-xs font-semibold text-blue-700 hover:text-blue-900">
                  Ver todos →
                </Link>
              </div>
              {data.leads.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[480px] text-left">
                    <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-5 py-3">Contacto</th>
                        <th className="px-4 py-3">Servicio</th>
                        <th className="px-4 py-3">Estado</th>
                        <th className="px-5 py-3 text-right">Recibido</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.leads.map((lead) => (
                        <tr key={lead.id} className="transition hover:bg-slate-50/70">
                          <td className="px-5 py-3.5 text-sm font-semibold text-slate-800">{lead.name}</td>
                          <td className="max-w-36 truncate px-4 py-3.5 text-xs text-slate-600">
                            {lead.service || "No indicado"}
                          </td>
                          <td className="px-4 py-3.5">
                            <span
                              className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${lead.status === "nuevo" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}
                            >
                              {lead.status.replaceAll("_", " ")}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-right text-xs text-slate-500">
                            {formatDate(lead.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="px-5 py-8 text-sm text-slate-500">Todavía no hay leads recibidos.</p>
              )}
            </Card>
            <Card className="p-5 sm:p-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Lectura de negocio
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-950">Datos disponibles</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Los leads reflejan consultas comerciales, y el directorio refleja registros editoriales. El
                sistema aún no registra pagos ni servicios contratados con importes confirmados.
              </p>
              <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-slate-400" />
                  <p className="text-sm font-semibold text-slate-800">Ingresos no disponibles</p>
                </div>
                <p className="mt-1.5 text-xs leading-5 text-slate-500">
                  No existe una fuente de transacciones en el modelo actual; por eso no se proyectan ingresos,
                  ventas ni clientes pagadores.
                </p>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <Link
                  to="/directorio"
                  className="rounded-xl border border-slate-200 p-3 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
                >
                  Gestionar directorio{" "}
                  <span className="block pt-1 text-xs font-normal text-slate-500">
                    Negocios y solicitudes
                  </span>
                </Link>
                <Link
                  to="/leads"
                  className="rounded-xl border border-slate-200 p-3 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
                >
                  Atender leads{" "}
                  <span className="block pt-1 text-xs font-normal text-slate-500">Consultas recibidas</span>
                </Link>
              </div>
            </Card>
          </section>
          <p className="px-1 text-[11px] leading-5 text-slate-400">
            Definiciones: “publicados” sigue la vista editorial pública; “incorporaciones” usa created_at de
            negocios y excluye ediciones; “leads” cuenta solicitudes entrantes y su estado actual. No se
            estima crecimiento porcentual ni ingresos.
          </p>
        </>
      ) : null}
    </div>
  );
}
